import AdminPageHeader from "@/components/admin/page-header";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Save } from "lucide-react";
import api, { fileUrl, apiErrorMessage } from "@/lib/api";
import { useImageUpload, IMAGE_ACCEPT, UPLOAD_HINT } from "@/hooks/use-image-upload";
import { useOutlet } from "@/context/OutletContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export default function AdminSettings() {
  const { activeOutlet, setActiveOutlet, refresh } = useOutlet();
  const [form, setForm] = useState(activeOutlet);
  const { uploading, upload } = useImageUpload();
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(activeOutlet); }, [activeOutlet]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogo = (e) =>
    upload(e, { folder: "logo", onUploaded: (id) => set("logo_file_id", id), successMessage: "Logo terupload" });

  const handleHeaderImage = (e) =>
    upload(e, { folder: "logo", onUploaded: (id) => set("header_image_file_id", id), successMessage: "Header image terupload" });

  const handleLoginImage = (e) =>
    upload(e, { folder: "logo", onUploaded: (id) => set("login_image_file_id", id), successMessage: "Gambar halaman login terupload" });

  const handlePromotionImage = (e) =>
    upload(e, { folder: "menu", onUploaded: (id) => set("promotion_image_file_id", id), successMessage: "Gambar promosi terupload" });

  const save = async () => {
    if (!activeOutlet?.id) return;
    setSaving(true);
    try {
       const payload = {
         name: form.name,
         tagline: form.tagline,
         phone: form.phone,
         address: form.address,
         hours: form.hours,
         logo_file_id: form.logo_file_id || "",
         logo_url: form.logo_url || "",
         header_image_file_id: form.header_image_file_id || "",
         header_image_url: form.header_image_url || "",
         login_image_file_id: form.login_image_file_id || "",
         login_image_url: form.login_image_url || "",
         primary_color: form.primary_color,
         tax_percent: Number(form.tax_percent) || 0,
         service_percent: Number(form.service_percent) || 0,
         currency: form.currency,
         payment_note: form.payment_note,
         menu_open: !!form.menu_open,
         closed_message: form.closed_message || "",
         popup_enabled: !!form.popup_enabled,
         popup_title: form.popup_title || "",
         popup_message: form.popup_message || "",
         promotion_enabled: !!form.promotion_enabled,
         promotion_title: form.promotion_title || "",
         promotion_description: form.promotion_description || "",
         promotion_image_file_id: form.promotion_image_file_id || "",
         promotion_image_url: form.promotion_image_url || "",
       };
      const { data } = await api.put(`/outlets/${activeOutlet.id}`, payload);
      setActiveOutlet((cur) => ({ ...cur, ...data }));
      toast.success("Pengaturan tersimpan");
      await refresh();
    } catch (err) { toast.error(apiErrorMessage(err, "Gagal menyimpan")); }
    finally { setSaving(false); }
  };

   const logoPreview = form?.logo_file_id ? fileUrl(form.logo_file_id) : form?.logo_url;
   const headerPreview = form?.header_image_file_id ? fileUrl(form.header_image_file_id) : form?.header_image_url;
   const loginPreview = form?.login_image_file_id ? fileUrl(form.login_image_file_id) : form?.login_image_url;
   const promotionPreview = form?.promotion_image_file_id ? fileUrl(form.promotion_image_file_id) : form?.promotion_image_url;

  // Status buka/tutup yang sedang berlaku — dihitung backend dari Jam Buka + toggle manual.
  const isOutletOpenNow = form?.is_open_now !== undefined && form?.is_open_now !== null
    ? form.is_open_now === true
    : !!form?.menu_open;
  let openStateLabel = "Mode manual — isi Jam Buka untuk mengaktifkan buka/tutup otomatis.";
  if (form?.open_auto) {
    if (form.open_reason === "manual_closed") {
      openStateLabel = "Ditutup manual oleh admin, walau masih dalam jam buka.";
    } else if (form.open_reason === "outside_hours") {
      openStateLabel = `Di luar jam buka (${form?.hours || "-"}).`;
    } else {
      openStateLabel = `Otomatis mengikuti jam buka (${form?.hours || "-"}).`;
    }
  }

  return (
    <div className="admin-page space-y-6" data-testid="admin-settings-page">
      <AdminPageHeader title="Pengaturan Outlet" description={<>Mengedit: <b>{activeOutlet?.name}</b> ({activeOutlet?.slug})</>} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <Section title="Logo & Brand">
            <div>
              <Label>Logo Outlet</Label>
              <div className="mt-2 border border-border rounded-xl p-4 bg-accent/30">
                <div className="aspect-video bg-white rounded-lg border border-border overflow-hidden grid place-items-center">
                  {logoPreview ? (
                    <img src={logoPreview} alt="logo" className="max-w-full max-h-full object-contain p-4" />
                  ) : (
                    <p className="text-xs text-muted-foreground">Belum ada logo</p>
                  )}
                </div>
                <div className="mt-3 flex gap-2">
                  <label className="flex-1">
                    <div className="border border-dashed border-border rounded-lg py-2 px-3 text-xs text-center hover:bg-white cursor-pointer transition-colors">
                      <Upload className="w-3.5 h-3.5 inline mr-1" />
                      {uploading ? "Uploading..." : "Ganti Logo"}
                      <span className="block text-[11px] text-muted-foreground/70 mt-0.5">{UPLOAD_HINT}</span>
                    </div>
                    <input type="file" accept={IMAGE_ACCEPT} className="sr-only" disabled={uploading} onChange={handleLogo} data-testid="logo-upload"/>
                  </label>
                  {form?.logo_file_id && (
                    <Button variant="outline" size="sm" onClick={() => set("logo_file_id", "")} aria-label="Hapus logo" data-testid="clear-logo-btn">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <Label>Header Menu Outlet</Label>
              <div className="mt-2 border border-border rounded-xl p-4 bg-accent/30">
                <div className="aspect-[16/7] bg-white rounded-lg border border-border overflow-hidden grid place-items-center">
                  {headerPreview ? (
                    <img src={headerPreview} alt="header menu" className="w-full h-full object-cover" />
                  ) : (
                    <p className="text-xs text-muted-foreground">Belum ada gambar header</p>
                  )}
                </div>
                <div className="mt-3">
                  <Input
                    value={form?.header_image_url || ""}
                    onChange={(e) => set("header_image_url", e.target.value)}
                    placeholder="Atau isi URL gambar header..."
                    className="bg-white"
                    aria-label="URL gambar header" data-testid="header-image-url-input"
                  />
                </div>
                <div className="mt-3 flex gap-2">
                  <label className="flex-1">
                    <div className="border border-dashed border-border rounded-lg py-2 px-3 text-xs text-center hover:bg-white cursor-pointer transition-colors">
                      <Upload className="w-3.5 h-3.5 inline mr-1" />
                      {uploading ? "Uploading..." : "Ganti Header"}
                      <span className="block text-[11px] text-muted-foreground/70 mt-0.5">{UPLOAD_HINT}</span>
                    </div>
                    <input type="file" accept={IMAGE_ACCEPT} className="sr-only" disabled={uploading} onChange={handleHeaderImage} data-testid="header-upload"/>
                  </label>
                  {form?.header_image_file_id && (
                    <Button variant="outline" size="sm" onClick={() => set("header_image_file_id", "")} aria-label="Hapus gambar header" data-testid="clear-header-btn">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <Label>Gambar Halaman Login</Label>
              <p className="text-xs text-muted-foreground mt-1">Gambar di panel kiri halaman login admin. Kosongkan untuk memakai gambar default.</p>
              <div className="mt-2 border border-border rounded-xl p-4 bg-accent/30">
                <div className="aspect-[3/4] bg-white rounded-lg border border-border overflow-hidden grid place-items-center">
                  {loginPreview ? (
                    <img src={loginPreview} alt="gambar halaman login" className="w-full h-full object-cover" data-testid="login-image-preview" />
                  ) : (
                    <p className="text-xs text-muted-foreground">Belum ada gambar — memakai gambar default</p>
                  )}
                </div>
                <div className="mt-3">
                  <Input
                    value={form?.login_image_url || ""}
                    onChange={(e) => set("login_image_url", e.target.value)}
                    placeholder="Atau isi URL gambar halaman login..."
                    className="bg-white"
                    aria-label="URL gambar login" data-testid="login-image-url-input"
                  />
                </div>
                <div className="mt-3 flex gap-2">
                  <label className="flex-1">
                    <div className="border border-dashed border-border rounded-lg py-2 px-3 text-xs text-center hover:bg-white cursor-pointer transition-colors">
                      <Upload className="w-3.5 h-3.5 inline mr-1" />
                      {uploading ? "Uploading..." : "Ganti Gambar Login"}
                      <span className="block text-[11px] text-muted-foreground/70 mt-0.5">{UPLOAD_HINT}</span>
                    </div>
                    <input type="file" accept={IMAGE_ACCEPT} className="sr-only" disabled={uploading} onChange={handleLoginImage} data-testid="login-image-upload"/>
                  </label>
                  {(form?.login_image_file_id || form?.login_image_url) && (
                    <Button variant="outline" size="sm" onClick={() => { set("login_image_file_id", ""); set("login_image_url", ""); }} aria-label="Hapus gambar login" data-testid="clear-login-image-btn">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <Label>Warna Primer</Label>
              <div className="flex items-center gap-2 mt-1">
                <input aria-label="Pilih warna utama" type="color" value={form?.primary_color || "#D14D36"} onChange={(e) => set("primary_color", e.target.value)}
                       className="w-11 h-11 rounded-lg border border-border cursor-pointer bg-white" data-testid="primary-color-input"/>
                <Input aria-label="Kode warna utama" value={form?.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)}
                       placeholder="#D14D36" className="bg-white flex-1"/>
              </div>
            </div>
          </Section>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <Section title="Informasi Outlet">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="adminsettings-field-1">Nama Outlet</Label>
                <Input id="adminsettings-field-1" value={form?.name || ""} onChange={(e) => set("name", e.target.value)}
                       className="bg-white" data-testid="outlet-name-input"/>
              </div>
              <div>
                <Label htmlFor="adminsettings-field-2">Tagline</Label>
                <Input id="adminsettings-field-2" value={form?.tagline || ""} onChange={(e) => set("tagline", e.target.value)}
                       className="bg-white" data-testid="tagline-input"/>
              </div>
              <div>
                <Label htmlFor="adminsettings-field-3">Nomor Telepon</Label>
                <Input id="adminsettings-field-3" value={form?.phone || ""} onChange={(e) => set("phone", e.target.value)}
                       className="bg-white" data-testid="phone-input"/>
              </div>
              <div>
                <Label htmlFor="adminsettings-field-4">Jam Buka</Label>
                <Input id="adminsettings-field-4" value={form?.hours || ""} onChange={(e) => set("hours", e.target.value)}
                       placeholder="08:00 - 22:00" className="bg-white" data-testid="hours-input"/>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Format <span className="font-medium">HH:MM - HH:MM</span> (contoh: 08:00 - 22:00). Outlet otomatis
                  tutup di luar jam ini. Kosongkan bila ingin mengatur buka/tutup sepenuhnya manual.
                </p>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="adminsettings-field-5">Alamat</Label>
                <Textarea id="adminsettings-field-5" rows={2} value={form?.address || ""} onChange={(e) => set("address", e.target.value)}
                          className="bg-white" data-testid="address-input"/>
              </div>
            </div>
          </Section>

          <Section title="Pajak & Layanan">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="adminsettings-field-6">Pajak (%)</Label>
                <Input id="adminsettings-field-6" type="number" step="0.01" value={form?.tax_percent ?? 0}
                       onChange={(e) => set("tax_percent", e.target.value)}
                       className="bg-white" data-testid="tax-percent-input"/>
              </div>
              <div>
                <Label htmlFor="adminsettings-field-7">Service Charge (%)</Label>
                <Input id="adminsettings-field-7" type="number" step="0.01" value={form?.service_percent ?? 0}
                       onChange={(e) => set("service_percent", e.target.value)}
                       className="bg-white" data-testid="service-percent-input"/>
              </div>
              <div>
                <Label htmlFor="adminsettings-field-8">Mata Uang</Label>
                <Input id="adminsettings-field-8" value={form?.currency || "IDR"} onChange={(e) => set("currency", e.target.value)}
                       className="bg-white" data-testid="currency-input"/>
              </div>
            </div>
            <div>
              <Label htmlFor="adminsettings-field-9">Catatan Pembayaran</Label>
              <Textarea id="adminsettings-field-9" rows={2} value={form?.payment_note || ""} onChange={(e) => set("payment_note", e.target.value)}
                        placeholder="Contoh: Pembayaran di kasir setelah selesai makan"
                        className="bg-white" data-testid="payment-note-input"/>
            </div>
          </Section>

          <Section title="Akses Menu Outlet & Popup">
            <div className="space-y-4">
              <div className="rounded-lg border border-border p-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">Menu Outlet Buka</p>
                    <p className="text-xs text-muted-foreground">
                      Status buka/tutup otomatis mengikuti Jam Buka. Matikan tombol ini untuk menutup outlet lebih awal
                      (tutup manual).
                    </p>
                  </div>
                  <Switch aria-label="Menu pemesanan dibuka" checked={!!form?.menu_open} onCheckedChange={(v) => set("menu_open", v)} data-testid="menu-open-switch"/>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      isOutletOpenNow ? "bg-primary/10 text-primary" : "bg-warning/15 text-warning"
                    }`}
                    data-testid="outlet-open-status"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${isOutletOpenNow ? "bg-primary" : "bg-warning"}`}/>
                    Status sekarang: {isOutletOpenNow ? "Buka" : "Tutup"}
                  </span>
                  <span className="text-[11px] text-muted-foreground">{openStateLabel}</span>
                </div>
              </div>
              <div>
                <Label htmlFor="adminsettings-field-10">Pesan Saat Menu Tutup</Label>
                <Textarea id="adminsettings-field-10"
                  rows={2}
                  value={form?.closed_message || ""}
                  onChange={(e) => set("closed_message", e.target.value)}
                  className="bg-white"
                  placeholder="Contoh: Maaf, outlet sedang tutup. Buka kembali pukul 10:00."
                  data-testid="closed-message-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Tampil ke pelanggan saat outlet tutup manual maupun otomatis di luar jam buka.
                </p>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Popup Informasi Menu</p>
                  <p className="text-xs text-muted-foreground">Popup tampil saat pelanggan membuka halaman menu outlet.</p>
                </div>
                <Switch aria-label="Aktifkan popup" checked={!!form?.popup_enabled} onCheckedChange={(v) => set("popup_enabled", v)} data-testid="popup-enabled-switch"/>
              </div>
              {form?.popup_enabled && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="adminsettings-field-11">Judul Popup</Label>
                    <Input id="adminsettings-field-11"
                      value={form?.popup_title || ""}
                      onChange={(e) => set("popup_title", e.target.value)}
                      className="bg-white"
                      placeholder="Contoh: Info Promo Hari Ini"
                      data-testid="popup-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="adminsettings-field-12">Isi Popup</Label>
                    <Textarea id="adminsettings-field-12"
                      rows={3}
                      value={form?.popup_message || ""}
                      onChange={(e) => set("popup_message", e.target.value)}
                      className="bg-white"
                      placeholder="Contoh: Diskon 10% untuk semua minuman sampai jam 17.00."
                      data-testid="popup-message-input"
                    />
                  </div>
                </div>
               )}
             </div>
           </Section>

           <Section title="Media Promosi">
             <div className="space-y-4">
               <div className="flex items-center justify-between rounded-lg border border-border p-3">
                 <div>
                   <p className="text-sm font-medium">Tampilkan Promosi Popup</p>
                   <p className="text-xs text-muted-foreground">Popup promosi tampil saat pelanggan membuka halaman menu outlet.</p>
                 </div>
                 <Switch aria-label="Aktifkan promosi" checked={!!form?.promotion_enabled} onCheckedChange={(v) => set("promotion_enabled", v)} data-testid="promotion-enabled-switch"/>
               </div>
               {form?.promotion_enabled && (
                 <div className="space-y-3">
                   <div>
                     <Label>Gambar Promosi</Label>
                     <div className="mt-2 border border-border rounded-xl p-4 bg-accent/30">
                       <div className="aspect-video bg-white rounded-lg border border-border overflow-hidden grid place-items-center">
                         {promotionPreview ? (
                           <img src={promotionPreview} alt="promosi" className="w-full h-full object-cover" />
                         ) : (
                           <p className="text-xs text-muted-foreground">Belum ada gambar promosi</p>
                         )}
                       </div>
                       <div className="mt-3">
                         <Input
                           value={form?.promotion_image_url || ""}
                           onChange={(e) => set("promotion_image_url", e.target.value)}
                           placeholder="Atau isi URL gambar promosi..."
                           className="bg-white"
                           aria-label="URL gambar promosi" data-testid="promotion-image-url-input"
                         />
                       </div>
                       <div className="mt-3 flex gap-2">
                         <label className="flex-1">
                           <div className="border border-dashed border-border rounded-lg py-2 px-3 text-xs text-center hover:bg-white cursor-pointer transition-colors">
                             <Upload className="w-3.5 h-3.5 inline mr-1" />
                             {uploading ? "Uploading..." : "Unggah Gambar"}
                             <span className="block text-[11px] text-muted-foreground/70 mt-0.5">{UPLOAD_HINT}</span>
                           </div>
                           <input type="file" accept={IMAGE_ACCEPT} className="sr-only" disabled={uploading} onChange={handlePromotionImage} data-testid="promotion-upload"/>
                         </label>
                         {form?.promotion_image_file_id && (
                           <Button variant="outline" size="sm" onClick={() => set("promotion_image_file_id", "")} aria-label="Hapus gambar promosi" data-testid="clear-promotion-image-btn">
                             <X className="w-3.5 h-3.5" />
                           </Button>
                         )}
                       </div>
                     </div>
                   </div>
                   <div>
                     <Label htmlFor="adminsettings-field-13">Judul Promosi</Label>
                     <Input id="adminsettings-field-13"
                       value={form?.promotion_title || ""}
                       onChange={(e) => set("promotion_title", e.target.value)}
                       className="bg-white"
                       placeholder="Contoh: Promo Spesial Hari Ini"
                       data-testid="promotion-title-input"
                     />
                   </div>
                   <div>
                     <Label htmlFor="adminsettings-field-14">Deskripsi Promosi</Label>
                     <Textarea id="adminsettings-field-14"
                       rows={2}
                       value={form?.promotion_description || ""}
                       onChange={(e) => set("promotion_description", e.target.value)}
                       className="bg-white"
                       placeholder="Contoh: Dapatkan diskon 20% untuk semua menu utama! Berlaku hingga akhir bulan."
                       data-testid="promotion-description-input"
                     />
                   </div>
                 </div>
               )}
             </div>
           </Section>

           <div className="flex justify-end sticky bottom-4">
            <Button onClick={save} disabled={saving} className="rounded-full h-11 px-6 shadow-warm-lg" data-testid="save-settings-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Menyimpan..." : "Simpan Perubahan"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="surface-card p-5 space-y-4">
      <h2 className="font-display font-bold text-lg">{title}</h2>
      {children}
    </div>
  );
}
